import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
    MessageSquare, 
    Send,
    Search,
    ArrowLeft,
    User,
    Megaphone,
    Users,
    Mail,
    Bell,
    X,
    Check
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MemberMessagesPage = () => {
    const { member } = useAuth();
    const [searchParams] = useSearchParams();
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const messagesEndRef = useRef(null);
    const pollIntervalRef = useRef(null);
    
    // Broadcast state
    const [broadcasts, setBroadcasts] = useState([]);
    const [unreadBroadcasts, setUnreadBroadcasts] = useState(0);
    const [showBroadcastModal, setShowBroadcastModal] = useState(false);
    const [showAnnouncementsView, setShowAnnouncementsView] = useState(false);
    const [broadcastForm, setBroadcastForm] = useState({
        title: '',
        content: '',
        sendAsConversations: false,
        sendEmailNotifications: false
    });
    const [sendingBroadcast, setSendingBroadcast] = useState(false);

    useEffect(() => {
        fetchConversations();
        fetchBroadcasts();
        fetchUnreadBroadcastCount();
        return () => {
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const conversationId = searchParams.get('conversation');
        if (conversationId && conversations.length > 0) {
            const conv = conversations.find(c => c.id === conversationId);
            if (conv) {
                selectConversation(conv);
            }
        }
    }, [searchParams, conversations]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchConversations = async () => {
        try {
            const response = await axios.get(`${API}/member/conversations`);
            setConversations(response.data);
        } catch (error) {
            console.error('Error fetching conversations:', error);
            toast.error('Failed to load conversations');
        } finally {
            setLoading(false);
        }
    };

    const fetchBroadcasts = async () => {
        try {
            const response = await axios.get(`${API}/member/broadcasts`);
            setBroadcasts(response.data);
        } catch (error) {
            console.error('Error fetching broadcasts:', error);
        }
    };

    const fetchUnreadBroadcastCount = async () => {
        try {
            const response = await axios.get(`${API}/member/broadcasts/unread-count`);
            setUnreadBroadcasts(response.data.count);
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    };

    const handleSendBroadcast = async () => {
        if (!broadcastForm.content.trim()) {
            toast.error('Please enter a message');
            return;
        }

        setSendingBroadcast(true);
        try {
            const response = await axios.post(`${API}/member/broadcasts`, {
                title: broadcastForm.title || null,
                content: broadcastForm.content,
                send_as_conversations: broadcastForm.sendAsConversations,
                send_email_notifications: broadcastForm.sendEmailNotifications
            });

            if (response.data.success) {
                toast.success(`Broadcast sent to ${response.data.recipients_count} members!`);
                setShowBroadcastModal(false);
                setBroadcastForm({
                    title: '',
                    content: '',
                    sendAsConversations: false,
                    sendEmailNotifications: false
                });
                fetchBroadcasts();
                if (broadcastForm.sendAsConversations) {
                    fetchConversations();
                }
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send broadcast');
        } finally {
            setSendingBroadcast(false);
        }
    };

    const markBroadcastAsRead = async (broadcastId) => {
        try {
            await axios.post(`${API}/member/broadcasts/${broadcastId}/read`);
            setBroadcasts(prev => prev.map(b => 
                b.id === broadcastId ? { ...b, is_read: true } : b
            ));
            setUnreadBroadcasts(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Error marking broadcast as read:', error);
        }
    };

    const selectConversation = async (conversation) => {
        setSelectedConversation(conversation);
        
        // Clear existing poll
        if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
        }

        try {
            const response = await axios.get(`${API}/member/messages/${conversation.id}`);
            setMessages(response.data);
            
            // Start polling for new messages
            pollIntervalRef.current = setInterval(async () => {
                const newResponse = await axios.get(`${API}/member/messages/${conversation.id}`);
                setMessages(newResponse.data);
            }, 5000);
        } catch (error) {
            console.error('Error fetching messages:', error);
            toast.error('Failed to load messages');
        }
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedConversation) return;

        setSendingMessage(true);
        try {
            await axios.post(`${API}/member/messages`, {
                conversation_id: selectedConversation.id,
                content: newMessage.trim()
            });
            setNewMessage('');
            
            // Refresh messages
            const response = await axios.get(`${API}/member/messages/${selectedConversation.id}`);
            setMessages(response.data);
            
            // Update conversation list
            fetchConversations();
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message');
        } finally {
            setSendingMessage(false);
        }
    };

    const getOtherParticipant = (conversation) => {
        if (!conversation?.participants) return null;
        return conversation.participants.find(p => p.username !== member?.username);
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;
        
        if (diff < 86400000) { // Less than 24 hours
            return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        } else if (diff < 604800000) { // Less than a week
            return date.toLocaleDateString('en-US', { weekday: 'short' });
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    const filteredConversations = conversations.filter(conv => {
        if (!searchQuery.trim()) return true;
        const other = getOtherParticipant(conv);
        return other?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
               other?.username?.toLowerCase().includes(searchQuery.toLowerCase());
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-[#A0A0A0] animate-pulse">Loading messages...</div>
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-180px)]" data-testid="member-messages">
            <div className="flex flex-col lg:flex-row h-full gap-4">
                {/* Conversations List */}
                <div className={`${selectedConversation ? 'hidden lg:flex' : 'flex'} flex-col w-full lg:w-80 bg-[#4A5240] rounded-xl border border-[#5A6350] overflow-hidden`}>
                    <div className="p-4 border-b border-[#5A6350]">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-xl font-bold text-white font-['Playfair_Display'] flex items-center gap-2">
                                <MessageSquare className="w-5 h-5 text-[#C9A227]" />
                                Messages
                            </h2>
                            <div className="flex gap-2">
                                {/* Announcements Button */}
                                <button
                                    onClick={() => setShowAnnouncementsView(true)}
                                    className="relative p-2 bg-[#3D4435] hover:bg-[#5A6350] rounded-lg transition-colors"
                                    title="View Announcements"
                                >
                                    <Bell className="w-4 h-4 text-[#C9A227]" />
                                    {unreadBroadcasts > 0 && (
                                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                                            {unreadBroadcasts}
                                        </span>
                                    )}
                                </button>
                                {/* Broadcast Button */}
                                <button
                                    onClick={() => setShowBroadcastModal(true)}
                                    className="p-2 bg-[#C9A227] hover:bg-[#B8922A] rounded-lg transition-colors"
                                    title="Send to All Members"
                                    data-testid="broadcast-btn"
                                >
                                    <Megaphone className="w-4 h-4 text-[#3D4435]" />
                                </button>
                            </div>
                        </div>
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                            <Input
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search conversations..."
                                className="pl-9 bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227] h-9 text-sm"
                            />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {filteredConversations.length === 0 ? (
                            <div className="p-6 text-center">
                                <MessageSquare className="w-10 h-10 text-[#666] mx-auto mb-3" />
                                <p className="text-[#A0A0A0] text-sm">
                                    {conversations.length === 0 
                                        ? 'No conversations yet. Start one from the Member Directory!'
                                        : 'No conversations match your search'}
                                </p>
                            </div>
                        ) : (
                            filteredConversations.map((conv) => {
                                const other = getOtherParticipant(conv);
                                return (
                                    <button
                                        key={conv.id}
                                        onClick={() => selectConversation(conv)}
                                        className={`w-full p-4 flex items-center gap-3 hover:bg-[#5A6350] transition-colors border-b border-[#5A6350] text-left ${
                                            selectedConversation?.id === conv.id ? 'bg-[#5A6350]' : ''
                                        }`}
                                        data-testid={`conversation-${conv.id}`}
                                    >
                                        {other?.profile_image ? (
                                            <img 
                                                src={other.profile_image} 
                                                alt={other.name}
                                                className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                    e.target.nextElementSibling.style.display = 'flex';
                                                }}
                                            />
                                        ) : null}
                                        <div className={`w-12 h-12 rounded-full bg-[#C9A227] items-center justify-center text-[#3D4435] font-medium flex-shrink-0 ${other?.profile_image ? 'hidden' : 'flex'}`}>
                                            {other?.name?.charAt(0) || '?'}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between">
                                                <p className="text-white font-medium truncate">{other?.name || 'Unknown'}</p>
                                                {conv.last_message_at && (
                                                    <span className="text-[#666] text-xs ml-2 flex-shrink-0">
                                                        {formatTime(conv.last_message_at)}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-[#A0A0A0] text-sm truncate">
                                                {conv.last_message?.content || 'No messages yet'}
                                            </p>
                                        </div>
                                        {conv.unread_count > 0 && (
                                            <span className="w-5 h-5 bg-[#C9A227] rounded-full text-[#3D4435] text-xs flex items-center justify-center font-medium flex-shrink-0">
                                                {conv.unread_count}
                                            </span>
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Messages Area */}
                <div className={`${!selectedConversation ? 'hidden lg:flex' : 'flex'} flex-col flex-1 bg-[#4A5240] rounded-xl border border-[#5A6350] overflow-hidden`}>
                    {selectedConversation ? (
                        <>
                            {/* Chat Header */}
                            <div className="p-4 border-b border-[#5A6350] flex items-center gap-3">
                                <button
                                    onClick={() => setSelectedConversation(null)}
                                    className="lg:hidden p-2 hover:bg-[#5A6350] rounded-lg"
                                >
                                    <ArrowLeft className="w-5 h-5 text-[#A0A0A0]" />
                                </button>
                                {(() => {
                                    const other = getOtherParticipant(selectedConversation);
                                    return (
                                        <>
                                            {other?.profile_image ? (
                                                <img 
                                                    src={other.profile_image} 
                                                    alt={other.name}
                                                    className="w-10 h-10 rounded-full object-cover"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextElementSibling.style.display = 'flex';
                                                    }}
                                                />
                                            ) : null}
                                            <div className={`w-10 h-10 rounded-full bg-[#C9A227] items-center justify-center text-[#3D4435] font-medium ${other?.profile_image ? 'hidden' : 'flex'}`}>
                                                {other?.name?.charAt(0) || '?'}
                                            </div>
                                            <div>
                                                <p className="text-white font-medium">{other?.name || 'Unknown'}</p>
                                                <p className="text-[#666] text-xs">@{other?.username || 'unknown'}</p>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>

                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                                {messages.length === 0 ? (
                                    <div className="text-center py-12">
                                        <MessageSquare className="w-12 h-12 text-[#666] mx-auto mb-3" />
                                        <p className="text-[#A0A0A0]">No messages yet. Start the conversation!</p>
                                    </div>
                                ) : (
                                    messages.map((msg) => {
                                        const isOwn = msg.sender_id === member?.id;
                                        return (
                                            <div
                                                key={msg.id}
                                                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                                            >
                                                <div className={`flex items-end gap-2 max-w-[75%] ${isOwn ? 'flex-row-reverse' : ''}`}>
                                                    {!isOwn && (
                                                        <>
                                                            {msg.sender?.profile_image ? (
                                                                <img 
                                                                    src={msg.sender.profile_image} 
                                                                    alt=""
                                                                    className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextElementSibling.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div className={`w-8 h-8 rounded-full bg-[#5A6350] items-center justify-center text-[#A0A0A0] text-sm flex-shrink-0 ${msg.sender?.profile_image ? 'hidden' : 'flex'}`}>
                                                                {msg.sender?.name?.charAt(0) || '?'}
                                                            </div>
                                                        </>
                                                    )}
                                                    <div>
                                                        <div className={`px-4 py-2 rounded-2xl ${
                                                            isOwn 
                                                                ? 'bg-[#C9A227] text-[#3D4435]' 
                                                                : 'bg-[#3D4435] text-white'
                                                        }`}>
                                                            <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                                                        </div>
                                                        <p className={`text-[10px] text-[#666] mt-1 ${isOwn ? 'text-right' : ''}`}>
                                                            {formatTime(msg.created_at)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Message Input */}
                            <form onSubmit={sendMessage} className="p-4 border-t border-[#5A6350]">
                                <div className="flex gap-2">
                                    <Input
                                        value={newMessage}
                                        onChange={(e) => setNewMessage(e.target.value)}
                                        placeholder="Type a message..."
                                        className="flex-1 bg-[#3D4435] border-[#5A6350] text-white placeholder:text-[#666] focus:ring-[#C9A227]/30 focus:border-[#C9A227]"
                                        data-testid="message-input"
                                        disabled={sendingMessage}
                                    />
                                    <Button
                                        type="submit"
                                        disabled={!newMessage.trim() || sendingMessage}
                                        className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                                        data-testid="send-message-btn"
                                    >
                                        <Send className="w-5 h-5" />
                                    </Button>
                                </div>
                            </form>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="text-center">
                                <MessageSquare className="w-16 h-16 text-[#666] mx-auto mb-4" />
                                <h3 className="text-white font-semibold mb-2">Select a conversation</h3>
                                <p className="text-[#A0A0A0] text-sm">
                                    Choose a conversation from the list or start a new one from the Member Directory
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Broadcast Modal */}
            <Dialog open={showBroadcastModal} onOpenChange={setShowBroadcastModal}>
                <DialogContent className="bg-[#3D4435] text-white max-w-lg border-[#5A6350]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-[#C9A227]">
                            <Megaphone className="w-5 h-5" />
                            Send Announcement to All Members
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-[#A0A0A0]">Title (optional)</Label>
                            <Input
                                value={broadcastForm.title}
                                onChange={(e) => setBroadcastForm({ ...broadcastForm, title: e.target.value })}
                                placeholder="Announcement title..."
                                className="bg-[#4A5240] border-[#5A6350] text-white placeholder:text-[#666]"
                            />
                        </div>
                        <div>
                            <Label className="text-[#A0A0A0]">Message *</Label>
                            <textarea
                                value={broadcastForm.content}
                                onChange={(e) => setBroadcastForm({ ...broadcastForm, content: e.target.value })}
                                placeholder="Write your message to all members..."
                                className="w-full h-32 p-3 bg-[#4A5240] border border-[#5A6350] rounded-lg text-white placeholder:text-[#666] resize-none focus:ring-2 focus:ring-[#C9A227] focus:border-transparent"
                            />
                        </div>

                        {/* Options */}
                        <div className="space-y-3 pt-2">
                            <label className="flex items-center gap-3 p-3 bg-[#4A5240] rounded-lg cursor-pointer hover:bg-[#5A6350] transition-colors">
                                <input
                                    type="checkbox"
                                    checked={broadcastForm.sendAsConversations}
                                    onChange={(e) => setBroadcastForm({ ...broadcastForm, sendAsConversations: e.target.checked })}
                                    className="w-4 h-4 rounded text-[#C9A227]"
                                />
                                <div>
                                    <p className="text-white font-medium flex items-center gap-2">
                                        <Users className="w-4 h-4 text-[#C9A227]" />
                                        Send as Individual Conversations
                                    </p>
                                    <p className="text-[#A0A0A0] text-sm">Each member receives this in their inbox as a direct message from you</p>
                                </div>
                            </label>

                            <label className="flex items-center gap-3 p-3 bg-[#4A5240] rounded-lg cursor-pointer hover:bg-[#5A6350] transition-colors">
                                <input
                                    type="checkbox"
                                    checked={broadcastForm.sendEmailNotifications}
                                    onChange={(e) => setBroadcastForm({ ...broadcastForm, sendEmailNotifications: e.target.checked })}
                                    className="w-4 h-4 rounded text-[#C9A227]"
                                />
                                <div>
                                    <p className="text-white font-medium flex items-center gap-2">
                                        <Mail className="w-4 h-4 text-[#C9A227]" />
                                        Send Email Notifications
                                    </p>
                                    <p className="text-[#A0A0A0] text-sm">Notify all members via email about this announcement</p>
                                </div>
                            </label>
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                variant="outline"
                                onClick={() => setShowBroadcastModal(false)}
                                className="flex-1 border-[#5A6350] text-white hover:bg-[#4A5240]"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSendBroadcast}
                                disabled={!broadcastForm.content.trim() || sendingBroadcast}
                                className="flex-1 bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435]"
                                data-testid="send-broadcast-btn"
                            >
                                {sendingBroadcast ? 'Sending...' : 'Send to All Members'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Announcements View Modal */}
            <Dialog open={showAnnouncementsView} onOpenChange={setShowAnnouncementsView}>
                <DialogContent className="bg-[#3D4435] text-white max-w-2xl max-h-[80vh] border-[#5A6350]">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-[#C9A227]">
                            <Bell className="w-5 h-5" />
                            Announcements
                            {unreadBroadcasts > 0 && (
                                <Badge className="bg-red-500 text-white ml-2">{unreadBroadcasts} new</Badge>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto max-h-[60vh] space-y-4">
                        {broadcasts.length === 0 ? (
                            <div className="text-center py-8">
                                <Bell className="w-12 h-12 text-[#666] mx-auto mb-4" />
                                <p className="text-[#A0A0A0]">No announcements yet</p>
                            </div>
                        ) : (
                            broadcasts.map((broadcast) => (
                                <div
                                    key={broadcast.id}
                                    className={`p-4 rounded-lg border ${
                                        !broadcast.is_read && broadcast.sender_id !== member?.id
                                            ? 'bg-[#C9A227]/10 border-[#C9A227]'
                                            : 'bg-[#4A5240] border-[#5A6350]'
                                    }`}
                                    onClick={() => !broadcast.is_read && markBroadcastAsRead(broadcast.id)}
                                >
                                    <div className="flex items-start justify-between mb-2">
                                        <div>
                                            {broadcast.title && (
                                                <h4 className="text-white font-semibold">{broadcast.title}</h4>
                                            )}
                                            <p className="text-[#C9A227] text-sm">
                                                From: {broadcast.sender_name || 'Unknown'}
                                                {broadcast.is_pinned && (
                                                    <Badge className="ml-2 bg-amber-600 text-white text-xs">Admin</Badge>
                                                )}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {!broadcast.is_read && broadcast.sender_id !== member?.id && (
                                                <Badge className="bg-[#C9A227] text-[#3D4435] text-xs">New</Badge>
                                            )}
                                            <span className="text-[#666] text-xs">
                                                {new Date(broadcast.created_at).toLocaleDateString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: 'numeric',
                                                    minute: '2-digit'
                                                })}
                                            </span>
                                        </div>
                                    </div>
                                    <p className="text-white whitespace-pre-wrap">{broadcast.content}</p>
                                </div>
                            ))
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MemberMessagesPage;
